// The contact sheet's panel for one instrument (_notes/plan_instrument_refactor.md,
// Step C): the tag as its two drawn lines plus an outside instance letter, the
// line through the bubble, alarm levels and notes — with what they mean, parsed
// live by the server as you type (POST /api/instruments/parse).
import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import api from '../utils/api';
import { currentType, LINE_STYLES } from '../utils/contactSheet';
import { getLabelForType } from '../data/equipmentTypes';
import { checkboxSx } from './sheetStyles';

const MONO = "'IBM Plex Mono', monospace";
const LEVELS = ['HH', 'H', 'L', 'LL'];

const field = {
  width: '100%', height: '32px', boxSizing: 'border-box', px: '9px', borderRadius: '6px', fontFamily: MONO, fontSize: '14px',
  color: '#eaf0fb', background: '#0b1730', border: '1px solid rgba(120,150,200,.32)', outline: 'none',
  '&:focus': { borderColor: 'rgba(45,212,191,.7)' }, '&:disabled': { opacity: 0.4 },
};
const label = { fontSize: '11px', color: '#7f93b8', mb: '4px', letterSpacing: '.04em' };
const btn = {
  height: '30px', px: '12px', borderRadius: '7px', border: '1px solid rgba(120,150,200,.28)', background: 'transparent',
  color: '#cdd9ee', fontSize: '12.5px', fontFamily: "'Lexend', sans-serif", cursor: 'pointer',
  '&:hover:not(:disabled)': { borderColor: 'rgba(45,212,191,.6)' }, '&:disabled': { opacity: 0.4, cursor: 'default' },
};

// What a person has entered, from an element's stored facets.
export function formFromElement(el) {
  const inst = el.instrument || {};
  const tag = inst.tag || {};
  const ann = inst.annotations || {};
  return {
    top: tag.topLine || '', bottom: tag.bottomLine || '', suffix: tag.outsideSuffix || '', untagged: !!tag.untagged,
    lineStyle: inst.lineStyle?.value || 'unknown',
    alarms: (ann.alarmLevels || []).map(a => a.level).join('/'),
    notes: (ann.notes || []).join('; '),
  };
}

// The instrument facets to save, and the element name that goes with them
// ("PX N019-A"; empty when untagged).
export function facetsFromForm(f, el) {
  const old = el.instrument?.annotations || {};
  const levels = f.alarms.toUpperCase().split(/[\s/,]+/).filter(lv => LEVELS.includes(lv));
  const instrument = {
    tag: { topLine: f.untagged ? '' : f.top.trim(), bottomLine: f.untagged ? '' : f.bottom.trim(),
      outsideSuffix: f.untagged ? '' : f.suffix.trim().replace(/^-+/, '').toUpperCase(), untagged: f.untagged },
    lineStyle: f.lineStyle,
    annotations: { ...old, alarmLevels: levels.map(level => ({ level, setpoint: null })),
      notes: f.notes.split(';').map(n => n.trim()).filter(Boolean) },
  };
  const t = instrument.tag;
  const name = f.untagged ? '' : [t.topLine, t.bottomLine].filter(Boolean).join(' ') + (t.outsideSuffix ? `-${t.outsideSuffix}` : '');
  return { instrument, name };
}

export default function InstrumentPanel({ el, drawingId, onSave, onClose }) {
  const [form, setForm] = useState(() => formFromElement(el));
  const [derived, setDerived] = useState(el.instrument?.derived || null);
  const [saving, setSaving] = useState(false);
  const topRef = useRef(null);
  const shape = currentType(el.elementType);

  useEffect(() => { setForm(formFromElement(el)); setDerived(el.instrument?.derived || null); }, [el._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse as the person types (debounced); the server holds the letter tables.
  useEffect(() => {
    let live = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post('/api/instruments/parse',
          { elementType: el.elementType, drawing: drawingId, instrument: facetsFromForm(form, el).instrument });
        if (live) setDerived(data.derived);
      } catch { /* keep the last parse */ }
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [form, el.elementType, drawingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async (confirm) => {
    setSaving(true);
    try {
      const { instrument, name } = facetsFromForm(form, el);
      const payload = { instrument: confirm ? { ...instrument, reviewStatus: 'confirmed' } : instrument, name };
      if (confirm) Object.assign(payload, { elementType: shape, typeConfirmed: true });
      await onSave(el, payload);
    } finally { setSaving(false); }
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); save(e.metaKey || e.ctrlKey); }
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  const reasons = derived?.reviewReasons || [];
  const findings = (derived?.findings || []).filter(f => f.review || f.code === 'user_defined_letter');
  const diamond = shape === 'logic_diamond';

  return (
    <Box data-testid="instrument-panel" onKeyDown={onKeyDown}
      sx={{ width: 320, flexShrink: 0, borderLeft: '1px solid rgba(120,150,200,.18)', background: '#0d1a33', overflow: 'auto', p: '16px', boxSizing: 'border-box' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '12px' }}>
        <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#f4f8ff' }}>{getLabelForType(shape)}</Box>
        <Box component="button" onClick={onClose} aria-label="Close" sx={{ ...btn, border: 'none', px: '6px', height: '24px' }}>✕</Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 72px', gap: '8px', mb: '8px' }}>
        <Box>
          <Box sx={label}>{diamond ? 'TEXT' : 'TOP LINE'}</Box>
          <Box component="input" ref={topRef} aria-label="Top line" value={form.top} onChange={set('top')} disabled={form.untagged} sx={field} />
        </Box>
        <Box>
          <Box sx={label}>{diamond ? 'SECOND ROW' : 'BOTTOM LINE'}</Box>
          <Box component="input" aria-label="Bottom line" value={form.bottom} onChange={set('bottom')} disabled={form.untagged} sx={field} />
        </Box>
        <Box>
          <Box sx={label} title="A, B, D11… printed beside the bubble (parallel instances)">BESIDE</Box>
          <Box component="input" aria-label="Instance letter" value={form.suffix} maxLength={8} onChange={set('suffix')} disabled={form.untagged || diamond} sx={field} />
        </Box>
      </Box>
      <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#8fa3c6', mb: '14px', cursor: 'pointer' }}>
        <Box component="input" type="checkbox" checked={form.untagged} onChange={set('untagged')} aria-label="Untagged" sx={checkboxSx()} /> No tag drawn
      </Box>

      <Box sx={label}>LINE THROUGH THE {diamond ? 'DIAMOND' : 'BUBBLE'}</Box>
      <Box role="radiogroup" aria-label="Line style" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', mb: '14px' }}>
        {LINE_STYLES.map(ls => (
          <Box key={ls.value} component="button" role="radio" aria-checked={form.lineStyle === ls.value} title={ls.label} aria-label={ls.label}
            onClick={() => setForm(f => ({ ...f, lineStyle: ls.value }))}
            sx={{ ...btn, height: '28px', px: '8px', fontSize: '11.5px', textAlign: 'left',
              ...(form.lineStyle === ls.value ? { borderColor: '#2dd4bf', color: '#2dd4bf', background: 'rgba(45,212,191,.1)' } : {}) }}>
            <span style={{ fontFamily: MONO, marginRight: 6 }}>{ls.glyph}</span>{ls.short}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '8px', mb: '14px' }}>
        <Box>
          <Box sx={label}>ALARMS</Box>
          <Box component="input" aria-label="Alarm levels" placeholder="H/L" value={form.alarms} onChange={set('alarms')} sx={field} />
        </Box>
        <Box>
          <Box sx={label}>NOTES (; between)</Box>
          <Box component="input" aria-label="Notes" value={form.notes} onChange={set('notes')} sx={{ ...field, fontSize: '12.5px' }} />
        </Box>
      </Box>

      <Box data-testid="instrument-meaning" sx={{ borderRadius: '8px', background: 'rgba(120,150,200,.08)', p: '10px 12px', mb: '14px', fontSize: '12.5px', color: '#cdd9ee', lineHeight: 1.55 }}>
        {derived ? (
          <>
            <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#f4f8ff' }}>{derived.normalized || (derived.untagged ? 'untagged' : '—')}</Box>
            <Box>{derived.description || (derived.parseStatus === 'not_a_tag' ? 'Label (not a tag)' : '—')}</Box>
            {derived.loopKey && <Box sx={{ color: '#9fb2d4' }}>Loop <span style={{ fontFamily: MONO }}>{derived.loopKey}</span>{derived.equipmentReference ? ` · serves ${derived.equipmentReference}` : ''}</Box>}
            <Box sx={{ color: '#9fb2d4' }}>
              {derived.hostSystem?.replace(/_/g, ' ')} · {derived.location?.replace(/_/g, ' ')}
              {derived.operatorAccessible === false ? ' · not operator accessible' : ''}
            </Box>
            {derived.roles?.length > 0 && <Box sx={{ color: '#9fb2d4' }}>{derived.roles.join(', ').replace(/_/g, ' ')}</Box>}
            {(reasons.length > 0 || findings.length > 0) && (
              <Box sx={{ mt: '6px', color: '#f5a524', fontSize: '12px' }}>
                {reasons.map(r => <Box key={r}>⚑ {r.replace(/_/g, ' ')}</Box>)}
                {findings.map((f, i) => <Box key={i} sx={{ color: '#c9a35a' }}>{f.detail}</Box>)}
              </Box>
            )}
          </>
        ) : '—'}
      </Box>

      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Box component="button" disabled={saving} onClick={() => save(false)} sx={btn}>Save <span style={{ fontFamily: MONO, fontSize: 10.5, opacity: 0.7 }}>↵</span></Box>
        <Box component="button" disabled={saving} onClick={() => save(true)}
          sx={{ ...btn, background: '#2dd4bf', borderColor: '#2dd4bf', color: '#04211d', fontWeight: 600 }}>
          Save &amp; confirm <span style={{ fontFamily: MONO, fontSize: 10.5, opacity: 0.7 }}>⌘↵</span>
        </Box>
      </Box>
    </Box>
  );
}
